<template>
  <div class="mbr-pin">
    <div class="mbr-pin__boxes" aria-hidden="true">
      <span
        v-for="n in numInputs"
        :key="n"
        class="mbr-pin__box"
        :class="{ 'mbr-pin__box--active': focused && n - 1 === activeIndex }"
      >
        <template v-if="n <= value.length">
          <span v-if="masked" class="mbr-pin__dot"></span>
          <span v-else class="mbr-pin__digit">{{ value[n - 1] }}</span>
        </template>
        <span v-else-if="focused && n - 1 === activeIndex" class="mbr-pin__caret"></span>
      </span>
    </div>
    <!-- readonly + inputmode none: digits come from PasscodeKeypad or a physical keyboard, never the system keyboard -->
    <input
      ref="input"
      class="mbr-pin__input"
      type="text"
      inputmode="none"
      autocomplete="off"
      readonly
      :aria-label="ariaLabel || $t('passcode.passcode')"
      :value="value"
      @focus="onFocus"
      @blur="focused = false"
      @keydown="onKeydown"
      @paste.prevent="onPaste"
      @pin-key="onPinKey"
    />
  </div>
</template>

<script>
// Drop-in for the vue3-otp-input usages on passcode screens: same on-change /
// on-complete events and clearInput(), and the root still contains an <input>
// so `$refs.x.$el.querySelector('input').focus()` keeps working.
export default {
  name: "PasscodePin",
  props: {
    // Accepted so existing `v-model` bindings stay valid, but deliberately never
    // read or written: vue3-otp-input 0.3.6 starts empty and ignores v-model too,
    // and SignIn reuses form2.passcode across steps (a create-step value must not
    // pre-fill the enter-passcode boxes).
    modelValue: {
      type: String,
      default: "",
    },
    numInputs: {
      type: Number,
      default: 4,
    },
    masked: {
      type: Boolean,
      default: true,
    },
    shouldAutoFocus: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: "",
    },
  },
  emits: ["on-change", "on-complete"],
  data() {
    return {
      value: "",
      focused: false,
    };
  },
  computed: {
    activeIndex() {
      return this.value.length < this.numInputs ? this.value.length : -1;
    },
  },
  mounted() {
    if (this.shouldAutoFocus) {
      this.$nextTick(() => this.focus());
    }
  },
  methods: {
    setValue(value) {
      if (value === this.value) return;
      this.value = value;
      this.$emit("on-change", value);
      if (value.length === this.numInputs) {
        this.$emit("on-complete", value);
      }
    },
    press(key) {
      if (key === "backspace") {
        this.setValue(this.value.slice(0, -1));
      } else if (/^\d$/.test(key) && this.value.length < this.numInputs) {
        this.setValue(this.value + key);
      }
    },
    onKeydown(event) {
      if (/^\d$/.test(event.key)) {
        event.preventDefault();
        this.press(event.key);
      } else if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        this.press("backspace");
      }
    },
    onPaste(event) {
      const digits = (event.clipboardData?.getData("text") ?? "").replace(/\D/g, "");
      this.setValue((this.value + digits).slice(0, this.numInputs));
    },
    // Dispatched by PasscodeKeypad on the focused (or last focused) PIN input
    onPinKey(event) {
      this.press(event.detail);
    },
    onFocus() {
      this.focused = true;
      document.querySelectorAll(".mbr-pin__input[data-pin-last]").forEach((input) => input.removeAttribute("data-pin-last"));
      this.$refs.input.setAttribute("data-pin-last", "");
    },
    clearInput() {
      this.setValue("");
    },
    focus() {
      this.$refs.input.focus();
    },
  },
};
</script>

<style scoped>
.mbr-pin {
  position: relative;
  display: inline-flex;
}

.mbr-pin__boxes {
  display: flex;
  gap: 12px;
}

.mbr-pin__box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 72px;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-lg);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.mbr-pin__box--active {
  border: 2px solid var(--mbr-color-primary);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--mbr-color-primary) 15%, transparent);
}

.mbr-pin__dot {
  width: 14px;
  height: 14px;
  border-radius: var(--mbr-radius-full);
  background: var(--mbr-color-primary);
}

.mbr-pin__digit {
  font-family: var(--mbr-font-headline);
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  color: var(--mbr-color-ink-headline);
}

.mbr-pin__caret {
  width: 2px;
  height: 24px;
  background: var(--mbr-color-primary);
  animation: mbr-pin-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes mbr-pin-pulse {
  50% {
    opacity: 0.5;
  }
}

.mbr-pin__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  opacity: 0;
  font-size: 16px;
  caret-color: transparent;
  cursor: pointer;
}
</style>
